import { DayShortName, EntityCalendarResourceGroupBy, EntityCalendarResourceType, EntityCalendarView, EntityView, EntityViewCalendar, EntityViewCalendarResources, EntityViewCard, MetadataEntityFieldData } from '@jms/model';
import type { ViewSettingsConfig } from '../view.settings.model';
import { CALENDAR_GROUP_BY_OPTIONS, CALENDAR_TIMED, DisplayPanelCalendarConfig, SETTINGS_PANEL_NONE, toFieldOptions, toFieldOptionsWithNone } from '@workbuddy/ui/v2/blocks/settings.panel';
import { DEFAULT_SHOW_DAYS, DEFAULT_SLOT_MAX_TIME, DEFAULT_SLOT_MIN_TIME, DEFAULT_SLOT_MINUTES, isMultiDayView, isResourceView, isTimedView } from '@workbuddy/ui/v2/components/calendar.host';
import { resolveCalendarColorFields, resolveCalendarDateFields, resolveCalendarResourceFields, resolveCardFields, resolveDefaultCalendarResourceField } from '../../../state/features/with.metadata';

/** Raw `calendar.card` form-group values — the same slot shape the Kanban card picker emits. */
export interface CalendarCardFormValues {
  identifier?: string;
  title?: string;
  description?: string;
  status?: string;
  body?: string[];
  footer?: string[];
}

/** Raw `calendar` form-group values emitted by `buildCalendarFields`. */
export interface CalendarDisplayFormValues {
  view?: string;
  card?: CalendarCardFormValues;
  startField?: string;
  endField?: string;
  allDay?: string;
  colorField?: string;
  resourceField?: string;
  resourceGroupBy?: string;
  resourceSelectionMode?: 'all' | 'named' | 'teams';
  resourceIds?: string[];
  resourceTeamIds?: string[];
  showUnassigned?: boolean;
  slotMinTime?: string;
  slotMaxTime?: string;
  slotDuration?: string | number;
  showDays?: string | number;
  daysOfWeek?: string[];
}

/**
 * What seeding and folding a calendar config both need to know about the entity.
 *
 * One argument rather than four positional ones because the four always travel together and are
 * three of the same type — `(existing, dateFields, titleField, uiFields)` invited exactly the
 * mix-up its callers made, passing `uiFields` twice and `dateFields` where `uiFields` belonged.
 */
export interface CalendarSeedContext {
  /** Date-like fields eligible as the event anchor, in metadata order. */
  dateFields: MetadataEntityFieldData[];
  /** Every ui field, which the card seeder picks title and status slots from. */
  uiFields: MetadataEntityFieldData[];
  /** Field the chip titles itself from when the view names none. */
  titleField?: string | null;
}

function toOptionalField(value: string | null | undefined): string | undefined {

  return !value || value === SETTINGS_PANEL_NONE ? undefined : value;
}

/**
 * Field the Calendar view should anchor on when the user has not picked one. The first date-like
 * field in metadata order — `resolveUiFields` already orders primary fields first, so this lands on
 * the entity's headline date (job start, task due, …) rather than an audit stamp.
 */
function resolveDefaultCalendarStartField(dateFields: MetadataEntityFieldData[]): string | undefined {

  const first = dateFields?.[0];
  return first ? String(first.path ?? first.name) : undefined;
}

/** The card an untouched slot set looks like, so a seeded config always has every slot present. */
function emptyCard(): EntityViewCard {

  return { _id: '', name: '', identifier: SETTINGS_PANEL_NONE, title: SETTINGS_PANEL_NONE, description: SETTINGS_PANEL_NONE, status: SETTINGS_PANEL_NONE, body: [], footer: [], highlight: { _id: '', days: 0 } } as unknown as EntityViewCard;
}

/** True when a card carries no chosen slot at all — the shape a fresh Calendar view starts from. */
function isEmptyCard(card: Partial<EntityViewCard> | null | undefined): boolean {

  if (!card) return true;
  const named = [card.identifier, card.title, card.description, card.status].some(slot => !!slot && slot !== SETTINGS_PANEL_NONE);
  return !named && !card.body?.length && !card.footer?.length;
}

/**
 * The card a fresh Calendar view starts with. Same policy Kanban uses for a new card — the entity's
 * own title-eligible fields, with a status slot when it has one — so switching a view between
 * Kanban and Calendar does not hand the user a blank field picker either way. Body is left empty on
 * purpose: the compact event renderer prints body rows, and a fresh view should read as one line.
 */
function seedCard(uiFields: MetadataEntityFieldData[], titleField?: string | null): EntityViewCard {

  const card = emptyCard();
  const options = resolveCardFields(uiFields ?? []);
  const title = titleField && titleField !== SETTINGS_PANEL_NONE ? titleField : (options.title[0] ? String(options.title[0].path ?? options.title[0].name) : undefined);
  const status = options.status[0] ? String(options.status[0].path ?? options.status[0].name) : undefined;

  return {
    ...card,
    ...(title ? { title } : {}),
    ...(status ? { status } : {}),
  } as EntityViewCard;
}

/**
 * Seed applied when a view first switches its display to Calendar. Month + all-day is the layout
 * that renders with the least configuration: it needs only a start field, and its card carries the
 * title. Anything the view already stored survives — the seed only fills gaps.
 */
export function seedCalendarConfig(existing: Partial<EntityViewCalendar> | null | undefined, context: CalendarSeedContext): EntityViewCalendar {

  const view = existing?.view ?? EntityCalendarView.DayGridMonth;
  const startField = existing?.startField ?? resolveDefaultCalendarStartField(context.dateFields);
  const card = isEmptyCard(existing?.card) ? seedCard(context.uiFields?.length ? context.uiFields : context.dateFields, context.titleField) : existing!.card!;

  const seeded = {
    ...existing,
    _id: existing?._id ?? '',
    view,
    startField: startField as EntityViewCalendar['startField'],
    allDay: existing?.allDay ?? true,
    card: card as EntityViewCalendar['card'],
  } as EntityViewCalendar;

  // A resource axis is seeded ONLY when the layout the view opens in needs one. A month grid with a
  // silently seeded resource field is not wrong so much as surprising: the axis is invisible until
  // the user switches layout, at which point the calendar reshapes around a field they never chose.
  if (!seeded.resources?.field && isResourceView(view)) {
    const field = resolveDefaultCalendarResourceField(context.uiFields ?? []);
    if (field) seeded.resources = { ...(seeded.resources ?? {}), field: field as EntityViewCalendarResources['field'], lanes: seeded.resources?.lanes ?? {} };
  }

  return seeded;
}

/**
 * Folds the calendar form group back onto the model's stored calendar config. Optional rows map
 * `none` to absent so an unset end field or resource axis is dropped rather than persisted as the
 * sentinel string.
 *
 * Rows hidden by the chosen layout keep whatever the model already held, and are NOT rewritten.
 * That is the whole point of the conditional writes below: the panel does not clear a hidden row's
 * control, so folding it back unconditionally takes the stale value the user cannot see and writes
 * it over the real one — switching a saved timeline view to Month and back used to reset its
 * visible hours to the defaults for exactly that reason.
 */
export function calendarFormToConfig(existing: Partial<EntityViewCalendar> | null | undefined, values: CalendarDisplayFormValues | null | undefined, context: CalendarSeedContext): EntityViewCalendar {

  const seeded = seedCalendarConfig(existing, context);
  if (!values) return seeded;

  const view = (values.view as EntityCalendarView) ?? seeded.view;
  const endField = toOptionalField(values.endField) ?? (values.endField === SETTINGS_PANEL_NONE ? undefined : seeded.endField);
  const colorField = toOptionalField(values.colorField) ?? (values.colorField === SETTINGS_PANEL_NONE ? undefined : seeded.colorField as string | undefined);
  const resourceField = values.resourceField === undefined ? seeded.resources?.field as string | undefined : toOptionalField(values.resourceField);
  const resourceGroupBy = toOptionalField(values.resourceGroupBy) as EntityCalendarResourceGroupBy | undefined;

  const config: EntityViewCalendar = {
    ...seeded,
    view,
    startField: (toOptionalField(values.startField) ?? seeded.startField) as EntityViewCalendar['startField'],
    allDay: values.allDay ? values.allDay !== CALENDAR_TIMED : seeded.allDay,
    card: cardFormToCard(seeded.card, values.card),
    daysOfWeek: (values.daysOfWeek as DayShortName[] | undefined) ?? seeded.daysOfWeek,
  };

  // Timed rows exist only on layouts that draw time slots; week-count only on week-shaped ones.
  if (isTimedView(view)) {
    config.slotMinTime = values.slotMinTime ?? seeded.slotMinTime ?? DEFAULT_SLOT_MIN_TIME;
    config.slotMaxTime = values.slotMaxTime ?? seeded.slotMaxTime ?? DEFAULT_SLOT_MAX_TIME;
    config.slotDuration = Number(values.slotDuration) || seeded.slotDuration || DEFAULT_SLOT_MINUTES;
  }

  if (isMultiDayView(view)) config.showDays = Number(values.showDays) || seeded.showDays || DEFAULT_SHOW_DAYS;

  if (!config.daysOfWeek?.length) delete config.daysOfWeek;

  if (colorField) config.colorField = colorField as EntityViewCalendar['colorField'];
  else delete config.colorField;

  if (endField) config.endField = endField as EntityViewCalendar['endField'];
  else delete config.endField;

  // Resource settings are dormant on plain calendars. Do not rewrite them from hidden controls.
  if (!isResourceView(view)) return config;

  // The resource axis is policy plus a field. Keep the stored lane policy when the user only
  // re-points the field; drop the whole block when they clear it back to None.
  //
  // An empty `lanes` map means "no lane-type filtering", NOT "hide every lane": the board treats an
  // absent type as shown, so a config carrying `lanes: {}` is a fully working resource axis. Only a
  // type present with `show: false` hides its lanes.
  if (resourceField) {
    const resources: EntityViewCalendarResources = { ...(seeded.resources ?? { lanes: {} }), field: resourceField as EntityViewCalendarResources['field'] };
    // Lane grouping is an attribute of the resource ref, so it only means anything alongside a
    // resource field; clearing the dropdown back to None drops the key rather than storing 'none'.
    if (resourceGroupBy) resources.groupBy = resourceGroupBy;
    else if (values.resourceGroupBy !== undefined) delete resources.groupBy;
    if (seeded.resources?.field && resourceField !== seeded.resources.field) {
      // A selection belongs to its resource field. Hidden selection controls for the previous
      // field must not restrict a different axis.
      delete resources.selection;
    } else {
      if (values.resourceSelectionMode === 'named') resources.selection = { mode: 'named', ids: [...new Set(values.resourceIds ?? [])] };
      else if (values.resourceSelectionMode === 'teams') resources.selection = { mode: 'teams', ids: [...new Set(values.resourceTeamIds ?? [])] };
      else if (values.resourceSelectionMode === 'all') delete resources.selection;
      if (values.showUnassigned !== undefined) resources.lanes = { ...resources.lanes, [EntityCalendarResourceType.Unassigned]: { ...resources.lanes?.[EntityCalendarResourceType.Unassigned], show: values.showUnassigned } };
    }
    config.resources = resources;
  } else {
    delete config.resources;
  }

  return config;
}

/**
 * Folds the card slot form group back onto the stored card. Untouched slots keep what the view held,
 * so a row hidden behind another display tab never blanks a slot the user set elsewhere.
 */
function cardFormToCard(seeded: EntityViewCard, values: CalendarCardFormValues | null | undefined): EntityViewCard {

  if (!values) return seeded;

  return {
    ...seeded,
    identifier: (values.identifier ?? seeded.identifier) as EntityViewCard['identifier'],
    title: (values.title ?? seeded.title) as EntityViewCard['title'],
    description: (values.description ?? seeded.description) as EntityViewCard['description'],
    status: (values.status ?? seeded.status) as EntityViewCard['status'],
    body: (values.body ?? seeded.body ?? []) as EntityViewCard['body'],
    footer: (values.footer ?? seeded.footer ?? []) as EntityViewCard['footer'],
  };
}

/**
 * Builds the calendar rows' config from the view under edit plus the metadata field lists. Shared
 * by the board's quick Display Settings overlay and the view wizard's Display menu so both offer
 * the same options and the same current values.
 */
export function resolveCalendarPanelOptions(view: EntityView | Partial<EntityView> | null | undefined, uiFields: MetadataEntityFieldData[], allowedViews?: readonly EntityCalendarView[], selection?: ViewSettingsConfig['calendarResourceSelection']): DisplayPanelCalendarConfig {

  const dateFields = resolveCalendarDateFields(uiFields ?? []);
  const resourceFields = resolveCalendarResourceFields(uiFields ?? []);
  const colorFields = resolveCalendarColorFields(uiFields ?? []);
  const cardFields = resolveCardFields(uiFields ?? []);
  const calendar = view?.calendar;
  const card = calendar?.card;

  return {
    allowedViews,
    startFieldOptions: toFieldOptions(dateFields),
    endFieldOptions: toFieldOptionsWithNone(dateFields),
    resourceFieldOptions: toFieldOptionsWithNone(resourceFields),
    resourceGroupByOptions: CALENDAR_GROUP_BY_OPTIONS,
    colorFieldOptions: toFieldOptionsWithNone(colorFields),
    card: {
      identifier: toFieldOptionsWithNone(cardFields.identifier),
      status: toFieldOptionsWithNone(cardFields.status),
      title: toFieldOptionsWithNone(cardFields.title),
      description: toFieldOptionsWithNone(cardFields.description),
      body: toFieldOptions(cardFields.body),
      footer: toFieldOptions(cardFields.footer),
    },
    identifier: (card?.identifier as string | undefined) ?? SETTINGS_PANEL_NONE,
    status: (card?.status as string | undefined) ?? SETTINGS_PANEL_NONE,
    title: (card?.title as string | undefined) ?? SETTINGS_PANEL_NONE,
    description: (card?.description as string | undefined) ?? SETTINGS_PANEL_NONE,
    body: (card?.body as string[] | undefined) ?? [],
    footer: (card?.footer as string[] | undefined) ?? [],
    view: calendar?.view ?? EntityCalendarView.DayGridMonth,
    startField: (calendar?.startField as string | undefined) ?? resolveDefaultCalendarStartField(dateFields),
    endField: (calendar?.endField as string | undefined) ?? SETTINGS_PANEL_NONE,
    allDay: calendar?.allDay ?? true,
    colorField: (calendar?.colorField as string | undefined) ?? SETTINGS_PANEL_NONE,
    resourceField: (calendar?.resources?.field as string | undefined) ?? SETTINGS_PANEL_NONE,
    resourceGroupBy: calendar?.resources?.groupBy ?? SETTINGS_PANEL_NONE,
    ...(selection ? {
      resourceSelectionField: selection.field,
      resourceSelectionEntities: selection.entities,
      resourceTeamEntity: selection.teamEntity,
    } : {}),
    resourceSelectionMode: calendar?.resources?.selection?.mode ?? 'all',
    resourceIds: calendar?.resources?.selection?.mode === 'named' ? calendar.resources.selection.ids : [],
    resourceTeamIds: calendar?.resources?.selection?.mode === 'teams' ? calendar.resources.selection.ids : [],
    showUnassigned: calendar?.resources?.lanes?.[EntityCalendarResourceType.Unassigned]?.show !== false,
    slotMinTime: calendar?.slotMinTime ?? DEFAULT_SLOT_MIN_TIME,
    slotMaxTime: calendar?.slotMaxTime ?? DEFAULT_SLOT_MAX_TIME,
    slotDuration: calendar?.slotDuration ?? DEFAULT_SLOT_MINUTES,
    showDays: calendar?.showDays ?? DEFAULT_SHOW_DAYS,
    daysOfWeek: calendar?.daysOfWeek ?? [],
  };
}
